import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '@/services/api';

/**
 * Report linked to an execution (one report per EXE). Drives whether the card
 * shows the "+" (link a report) or the "Report Dashboard" button.
 */
export function useReportLink(executionId: string, enabled = true) {
  return useQuery({
    queryKey: ['report-link', executionId],
    queryFn: () => api.reports.getLink(executionId),
    enabled: enabled && !!executionId,
    staleTime: 30000,
  });
}

export function useSetReportLink(executionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reportId: string) =>
      api.reports.setLink(executionId, { reportId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-link', executionId] });
      toast.success('Report linked');
    },
    onError: (error) => {
      toast.error(`Failed to link report: ${error.message}`);
    },
  });
}

export function useDeleteReportLink(executionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.reports.deleteLink(executionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-link', executionId] });
      toast.success('Report unlinked');
    },
    onError: (error) => {
      toast.error(`Failed to unlink report: ${error.message}`);
    },
  });
}

/**
 * Download and parse the report payload (job-result.json) for a report id.
 */
export function useReport(reportId: string) {
  return useQuery({
    queryKey: ['report', reportId],
    queryFn: () => api.reports.get(reportId),
    enabled: !!reportId,
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}
